'use client';

import React, { useRef, useState } from 'react';
import { ChevronDown, BookOpen, Code2, History, Mail, ArrowUpRight } from 'lucide-react';
import { RESOURCE_LINKS } from '../constants';

/** Icon per resource destination (keyed by href so it stays in sync with
 *  RESOURCE_LINKS). Falls back to BookOpen for anything new. */
const ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  '/learn': BookOpen,
  '/developers': Code2,
  '/changelog': History,
  '/contact': Mail,
};

/**
 * Compact "Resources" dropdown in the top nav (replaces the old dead `#`
 * item). A small floating panel rather than the full-width Products mega-bar —
 * the resource list is short. Hover to open (with a close grace period),
 * click to toggle; every entry is a real page.
 */
const ResourcesMenu: React.FC<{ dark?: boolean }> = ({ dark = false }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const openMenu = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };
  const closeMenu = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
          open
            ? dark
              ? 'bg-white/10 text-white'
              : 'bg-slate-100 text-slate-900'
            : dark
              ? 'text-white/80 hover:text-white'
              : 'text-slate-600 hover:text-brand-600'
        }`}
      >
        Resources
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      <div
        className={`absolute left-0 top-full z-50 mt-3 w-[19rem] rounded-xl border border-slate-200/70 bg-white p-2 shadow-[0_28px_48px_-28px_rgba(15,23,42,0.25)] transition-all duration-200 ${
          open ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-1 opacity-0'
        }`}
      >
        {RESOURCE_LINKS.map((item) => {
          const Icon = ICONS[item.href] ?? BookOpen;
          return (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              className="group flex gap-3 rounded-lg p-3 transition-colors hover:bg-slate-50"
            >
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500 transition-colors group-hover:bg-brand-50 group-hover:text-brand-600">
                <Icon size={16} />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-sm font-semibold text-slate-900">{item.label}</span>
                  <ArrowUpRight
                    size={12}
                    className="text-slate-300 opacity-0 transition-opacity group-hover:opacity-100"
                  />
                </div>
                {'desc' in item && item.desc && (
                  <p className="mt-0.5 text-xs leading-snug text-slate-500">{item.desc}</p>
                )}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default ResourcesMenu;
